<template>
  <!-- Design: job panel → company tab (map, company card, its other jobs) -->
  <section class="p-4 p-lg-5">
    <img v-if="headerImage" class="ts-company-header-image" :src="headerImage" :alt="name" />
    <template v-else-if="address">
      <iframe class="ts-map" loading="lazy" :src="mapEmbedUrl" :title="`${mapLabel}: ${name}, ${address}`"></iframe>
      <div class="d-flex flex-wrap gap-2 mt-3">
        <a class="btn ts-btn-sm ts-btn-outline-accent" :href="mapUrl" target="_blank" rel="noopener">
          <i class="bi bi-arrows-fullscreen me-2" aria-hidden="true"></i>
          {{ text.mapEnlarge }}
        </a>
        <a class="btn ts-btn-sm ts-btn-outline-accent" :href="directionsUrl" target="_blank" rel="noopener">
          <i class="bi bi-signpost me-2" aria-hidden="true"></i>
          {{ text.route }}
        </a>
      </div>
    </template>

    <article class="ts-company-card d-flex flex-column flex-sm-row align-items-start gap-4 mt-3 p-4">
      <div class="ts-job-company-logo d-flex flex-shrink-0 align-items-center justify-content-center fw-bold overflow-hidden">
        <img v-if="logo" class="img-fluid" :src="logo" :alt="name" />
        <template v-else>{{ initials }}</template>
      </div>
      <div class="flex-grow-1 min-w-0">
        <div class="ts-panel-info-list mb-4">
          <div class="ts-panel-info-row">
            <i class="bi bi-building ts-panel-info-icon" aria-hidden="true"></i>
            <h2 class="ts-panel-info-text fw-bold mb-0" v-html="name"></h2>
          </div>
          <div v-if="address" class="ts-panel-info-row">
            <i class="bi bi-geo-alt ts-panel-info-icon" aria-hidden="true"></i>
            <span class="ts-panel-info-text">{{ address }}</span>
          </div>
          <div v-if="contactPerson" class="ts-panel-info-row">
            <i class="bi bi-person ts-panel-info-icon" aria-hidden="true"></i>
            <span class="ts-panel-info-text">{{ contactPerson }}</span>
          </div>
          <a v-if="phone" class="ts-panel-info-row ts-panel-info-action" :href="`tel:${phone}`">
            <i class="bi bi-telephone ts-panel-info-icon" aria-hidden="true"></i>
            <span class="ts-panel-info-text">{{ phone }}</span>
          </a>
          <a v-if="email" class="ts-panel-info-row ts-panel-info-action" :href="`mailto:${email}`">
            <i class="bi bi-envelope ts-panel-info-icon" aria-hidden="true"></i>
            <span class="ts-panel-info-text">{{ email }}</span>
          </a>
          <a v-if="homepage" class="ts-panel-info-row ts-panel-info-action" :href="homepageUrl" target="_blank" rel="noopener">
            <i class="bi bi-globe ts-panel-info-icon" aria-hidden="true"></i>
            <span class="ts-panel-info-text">{{ homepage }}</span>
          </a>
        </div>
        <template v-if="description">
          <h3 class="fw-bold mb-2">{{ text.aboutCompany }}</h3>
          <div class="ts-company-description" v-html="description"></div>
          <div v-if="descriptionLong" class="ts-company-description mt-3" v-html="descriptionLong"></div>
        </template>
        <button class="btn ts-btn-sm ts-btn-outline-accent mt-3" type="button" @click="$emit('show-jobs')">
          {{ allJobsLabel }}
          <i class="bi bi-arrow-right ms-2" aria-hidden="true"></i>
        </button>
      </div>
    </article>
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { JobContact } from '@/api/Job';
import { AppModule } from '@/store/modules/app';
import { jobAddress, mapEmbedUrl } from '@/sections/jobs/address';

const JOB_SERVER = 'https://job-server.net';

// Company data of a job ad: the portal's company profile (employer_info, filled for partner
// companies) with the contact data of the ad itself as the fallback.
@Component
export default class TsJobCompanyPane extends Vue {
  // employer_info
  @Prop({ default: () => ({}) }) readonly info!: Record<string, any>;

  @Prop({ default: null }) readonly contact!: JobContact | null;

  // From the result list, while the rest loads
  @Prop({ default: '' }) readonly fallbackName!: string;

  @Prop({ default: '' }) readonly fallbackLocation!: string;

  @Prop({ default: '' }) readonly allJobsLabel!: string;

  @Prop({ default: '' }) readonly mapLabel!: string;

  get text() {
    return {
      mapEnlarge: AppModule.translation[16566],
      route: AppModule.translation[2811],
      aboutCompany: AppModule.translation[16567],
    };
  }

  get name(): string {
    return this.info.firmname || this.contact?.company_name || this.fallbackName;
  }

  get initials(): string {
    const words = String(this.name).replace(/<[^>]*>/g, '').split(/\s+/).filter((word) => /^[A-Za-zÄÖÜäöü0-9]/.test(word));
    return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('');
  }

  get logo(): string {
    const url = this.info.logo_url;
    if (!url) return '';
    return url.startsWith('http') ? url : `${JOB_SERVER}${url}`;
  }

  get headerImage(): string {
    return this.info.company_header ? `${JOB_SERVER}${this.info.company_header}` : '';
  }

  get address(): string {
    return jobAddress(this.info, this.contact, this.fallbackLocation);
  }

  get contactPerson(): string {
    const { info, contact } = this;
    const fromProfile = [info.contact_person_salutation, info.contact_person_firstname, info.contact_person_lastname].filter(Boolean).join(' ');
    return fromProfile || [contact?.firstname, contact?.lastname].filter(Boolean).join(' ');
  }

  get phone(): string {
    return this.info.phone || this.contact?.phone || '';
  }

  get email(): string {
    return this.info.email || this.info.contact_person_email || this.contact?.email || '';
  }

  get homepage(): string {
    return this.info.homepage || this.contact?.homepage || '';
  }

  get homepageUrl(): string {
    return /^https?:\/\//.test(this.homepage) ? this.homepage : `https://${this.homepage}`;
  }

  get description(): string {
    return this.info.description || '';
  }

  get descriptionLong(): string {
    return this.info.description_long || '';
  }

  get mapQuery(): string {
    return encodeURIComponent(`${String(this.name).replace(/<[^>]*>/g, '')}, ${this.address}`);
  }

  get mapEmbedUrl(): string {
    return mapEmbedUrl(this.address);
  }

  get mapUrl(): string {
    return `https://www.google.com/maps/search/?api=1&query=${this.mapQuery}`;
  }

  get directionsUrl(): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(this.address)}`;
  }
}
</script>
